import React from 'react';
import {
    FormControl,
    Select,
    MenuItem,
    ListSubheader,
    Chip,
    Box,
    OutlinedInput,
    Typography,
} from '@mui/material';

/**
 * A grouped Select field for picking subsections, organized under their parent sections.
 *
 * Props:
 *  - sections: [{ section_id, section_name, enable_subsection }]
 *  - subSections: [{ subsection_id, subsection_name, section_id }]
 *  - value: array of selected subsection compound keys  "sectionId_subsectionId"
 *  - onChange: (selectedKeys: string[]) => void
 */
const SFSectionAndSubsectionSelectField = ({
    sections = [],
    subSections = [],
    value = [],
    onChange,
}) => {

    // Build a map: section_id -> subsection[]
    const subsectionsBySection = {};
    subSections.forEach(sub => {
        if (!subsectionsBySection[sub.section_id]) {
            subsectionsBySection[sub.section_id] = [];
        }
        subsectionsBySection[sub.section_id].push(sub);
    });

    // Build the menu items: section headers + subsection items
    const menuItems = [];
    sections.forEach(section => {
        const subs = subsectionsBySection[section.section_id] || [];

        // Section group header
        menuItems.push(
            <ListSubheader key={`header-${section.section_id}`} sx={{ fontWeight: 700, color: '#333', backgroundColor: '#f5f5f5' }}>
                {section.section_name}
            </ListSubheader>
        );

        if (subs.length === 0) {
            // No subsections available
            menuItems.push(
                <MenuItem key={`empty-${section.section_id}`} disabled>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No subsections
                    </Typography>
                </MenuItem>
            );
        } else {
            subs.forEach(sub => {
                const compoundKey = `${section.section_id}_${sub.subsection_id}`;
                menuItems.push(
                    <MenuItem key={compoundKey} value={compoundKey}>
                        {sub.subsection_name}
                    </MenuItem>
                );
            });
        }
    });

    const getSectionName = (compoundKey) => {
        const sectionId = Number(compoundKey.split('_')[0]);
        const section = sections.find(s => s.section_id === sectionId);
        return section ? section.section_name : '';
    };

    const getSubsectionName = (compoundKey) => {
        const [sectionId, subsectionId] = compoundKey.split('_').map(Number);
        const sub = subSections.find(
            s => s.section_id === sectionId && s.subsection_id === subsectionId
        );
        return sub ? sub.subsection_name : '';
    };

    const getLabel = (compoundKey) => {
        return `${getSectionName(compoundKey)} / ${getSubsectionName(compoundKey)}`;
    };

    return (
        <FormControl fullWidth style={{
            maxWidth: '550px',
        }}>
            <Select
                size="small"
                multiple
                value={value}
                onChange={(e) => onChange(e.target.value)}
                input={<OutlinedInput />}
                renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map(key => (
                            <Chip
                                key={key}
                                size="small"
                                className="custom-chip"
                                title={getLabel(key)}
                                sx={{ height: 'auto', maxWidth: 180, '& .MuiChip-label': { display: 'block', whiteSpace: 'normal', py: '4px' } }}
                                label={
                                    <span>
                                        <span style={{ fontSize: 10, display: 'block', lineHeight: 1.2 }}>
                                            {getSectionName(key)}
                                        </span>
                                        <span style={{ fontSize: 11, fontWeight: 600, display: 'block', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {getSubsectionName(key)}
                                        </span>
                                    </span>
                                }
                            />
                        ))}
                    </Box>
                )}
            >
                {menuItems}
            </Select>
        </FormControl>
    );
};

export default SFSectionAndSubsectionSelectField;
